import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import { flatten } from "flat";
import { useMemo, type PropsWithChildren } from "react";
import { IntlProvider } from "react-intl";

import { api } from "@/api/api";
import { IconContextProvider } from "@/app/contexts/IconContextProvider";
import { FeatureFlagsProvider } from "@/app/featureFlags/FeatureFlagsProvider";
import { DevToolsHub } from "@/components/DevToolsHub/DevToolsHub";
import { getDir } from "@/hooks/useLocaleInfo";
import { isProduction } from "@/utils/constants";
import { defaultLocale, isAllowedLocale, type Locale } from "@/utils/locale";

import defaultMessages from "../../messages/ar.json";

import { CacheProvider } from "./contexts/CacheProvider";
import { MuiThemeProvider } from "./contexts/MuiThemeProvider";
import { platformApi } from "./platform";

let locale: Locale = defaultLocale;
let messages = defaultMessages;

// TODO: modify this when we decide to support eng and ar versions on production
if (!isProduction) {
  try {
    const fetchedLocale = await platformApi.getLocale();

    locale = isAllowedLocale(fetchedLocale) ? fetchedLocale : defaultLocale;
    if (locale !== defaultLocale) {
      messages = (await import(`../../messages/${locale}.json`)).default;
    }
  } catch (error) {
    Sentry.captureException(error);
    // in case of error, we fallback to en locale for now, as it better for development
    locale = "en";
    if (locale !== defaultLocale) {
      messages = (await import(`../../messages/${locale}.json`)).default;
    }
  }
}

api.defaults.headers.common["Accept-Language"] = locale;
dayjs.locale(locale);
document.documentElement.dir = getDir(locale);

export const RouterWrapper = ({ children }: PropsWithChildren) => {
  const flattenedMessages = useMemo(() => {
    return flatten<typeof messages, Record<string, string>>(messages);
  }, []);

  return (
    <FeatureFlagsProvider>
      <IntlProvider locale={locale} messages={flattenedMessages}>
        <CacheProvider locale={locale}>
          <IconContextProvider>
            <MuiThemeProvider>
              {children}

              {!isProduction && <DevToolsHub />}
            </MuiThemeProvider>
          </IconContextProvider>
        </CacheProvider>
      </IntlProvider>
    </FeatureFlagsProvider>
  );
};
